import { TokenDebugInfo, getTokenDebugInfo } from "@core/utils/tokenValidator"

export type SessionExitReason =
  | "auto_logout_timeout"
  | "auto_logout_manual"
  | "token_invalid_before_request"
  | "token_not_valid_response"
  | "require_auth_missing_token"
  | "require_auth_invalid_token"

export interface SessionExitEvent {
  reason: SessionExitReason
  occurredAt: string
  route: string
  token: TokenDebugInfo
  details?: Record<string, unknown>
}

interface SessionExitFeedback {
  message: string
  type: "warning" | "error"
}

const LAST_SESSION_EXIT_KEY = "sigma:lastSessionExit"
const SESSION_EXIT_HISTORY_KEY = "sigma:sessionExitHistory"
const SESSION_EXIT_HISTORY_LIMIT = 30
const DEFAULT_RECENT_MAX_AGE_MS = 10 * 60 * 1000

const isBrowser = () => typeof window !== "undefined" && typeof window.localStorage !== "undefined"

const sanitizeLogValue = (value: unknown, visited = new WeakSet<object>()): unknown => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    }
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value === "bigint") {
    return value.toString()
  }

  if (Array.isArray(value)) {
    return value.map(item => sanitizeLogValue(item, visited))
  }

  if (value && typeof value === "object") {
    if (visited.has(value)) {
      return "[Circular]"
    }

    visited.add(value)

    const next: Record<string, unknown> = {}
    Object.entries(value).forEach(([key, nestedValue]) => {
      next[key] = sanitizeLogValue(nestedValue, visited)
    })
    return next
  }

  return value
}

const readJson = <T>(key: string): T | null => {
  if (!isBrowser()) {
    return null
  }

  const raw = localStorage.getItem(key)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

const writeJson = (key: string, value: unknown) => {
  if (!isBrowser()) {
    return
  }

  localStorage.setItem(key, JSON.stringify(value))
}

const getCurrentRoute = () => {
  if (!isBrowser()) {
    return ""
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

const formatDateTime = (iso?: string) => {
  if (!iso) {
    return null
  }

  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date.toLocaleString("ko-KR")
}

const formatMinutes = (milliseconds?: unknown) => {
  if (typeof milliseconds !== "number" || !Number.isFinite(milliseconds) || milliseconds <= 0) {
    return null
  }

  return Math.round(milliseconds / 1000 / 60)
}

export const stringifyLogData = (value: unknown): string => {
  try {
    return JSON.stringify(sanitizeLogValue(value))
  } catch {
    return String(value)
  }
}

export const recordSessionExit = (reason: SessionExitReason, details?: Record<string, unknown>): SessionExitEvent | null => {
  if (!isBrowser()) {
    return null
  }

  const payload: SessionExitEvent = {
    reason,
    occurredAt: new Date().toISOString(),
    route: getCurrentRoute(),
    token: getTokenDebugInfo(),
    ...(details ? { details: sanitizeLogValue(details) as Record<string, unknown> } : {}),
  }

  writeJson(LAST_SESSION_EXIT_KEY, payload)

  const currentHistory = readJson<SessionExitEvent[]>(SESSION_EXIT_HISTORY_KEY) || []
  writeJson(SESSION_EXIT_HISTORY_KEY, [payload, ...currentHistory].slice(0, SESSION_EXIT_HISTORY_LIMIT))

  console.warn(`[SessionExit] ${stringifyLogData(payload)}`)

  return payload
}

export const clearLastSessionExit = () => {
  if (!isBrowser()) {
    return
  }

  localStorage.removeItem(LAST_SESSION_EXIT_KEY)
}

export const consumeRecentSessionExit = (maxAgeMs: number = DEFAULT_RECENT_MAX_AGE_MS): SessionExitEvent | null => {
  const event = readJson<SessionExitEvent>(LAST_SESSION_EXIT_KEY)
  clearLastSessionExit()

  if (!event) {
    return null
  }

  const occurredAt = new Date(event.occurredAt).getTime()
  if (Number.isNaN(occurredAt)) {
    return null
  }

  if (Date.now() - occurredAt > maxAgeMs) {
    return null
  }

  return event
}

export const getSessionExitFeedback = (event: SessionExitEvent): SessionExitFeedback | null => {
  const sessionExpiryText = formatDateTime(event.token.expIso)
  const lastActivityText = formatDateTime(typeof event.details?.lastActivityAt === "string" ? event.details.lastActivityAt : undefined)
  const inactiveMinutes = formatMinutes(typeof event.details?.inactiveForMs === "number" ? event.details.inactiveForMs : undefined)

  switch (event.reason) {
    case "auto_logout_timeout": {
      const suffix = [
        inactiveMinutes ? `마지막 활동 이후 약 ${inactiveMinutes}분 동안 입력이 감지되지 않았습니다.` : null,
        lastActivityText ? `마지막 활동 시각: ${lastActivityText}` : null,
      ]
        .filter(Boolean)
        .join("\n")

      return {
        type: "warning",
        message: ["활동이 감지되지 않아 자동 로그아웃되었습니다.", suffix].filter(Boolean).join("\n"),
      }
    }

    case "token_invalid_before_request":
    case "token_not_valid_response":
    case "require_auth_missing_token":
    case "require_auth_invalid_token":
      return {
        type: "warning",
        message: [
          "로그인 세션이 만료되어 로그인 화면으로 이동했습니다.",
          sessionExpiryText ? `세션 만료 시각: ${sessionExpiryText}` : null,
          "특히 자정 전후에 반복되면 세션 만료 시간과 서버 시각을 함께 확인해 주세요.",
        ]
          .filter(Boolean)
          .join("\n"),
      }

    case "auto_logout_manual":
      return null

    default:
      return null
  }
}

